import { useEffect, useRef, useState } from 'react'
import { useAppSelector } from '@/app/hooks'
import { selectUser } from '@/features/auth/authSlice'
import { COMPANY_SPECIFIC_ROLES, PROPERTY_ROLES } from '@/constants/roles'
import { showError, showSuccess } from '@/lib/utils/notifications'
import { logoutAndRedirect } from '@/lib/api/authService'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import modalStyles from '@/features/application/styles/modals.module.css'
import {
  checkGdprConsent,
  submitGdprConsent,
} from '../services/documentManagementService'
import type { ServiceAgreementDocument } from '../types/document'
import { GDPR_CONSENT_PENDING_KEY } from '../constants/gdprConsent'
import { ServiceAgreementHtml } from './ServiceAgreementHtml'

export interface GdprConsentModalProps {
  isOpen: boolean
  onAccepted: () => void
}

/**
 * Service-agreement acknowledgement popup.
 * Uses the shared OnScreenModal header/footer chrome (same as Send Email / Edit Role).
 * Close is disabled — user must Accept or Decline.
 */
export function GdprConsentModal({ isOpen, onAccepted }: GdprConsentModalProps) {
  const user = useAppSelector(selectUser)
  const roleName = user?.current_role?.role_name ?? ''
  const contentRef = useRef<HTMLDivElement | null>(null)

  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [consentDoc, setConsentDoc] = useState<ServiceAgreementDocument | null>(null)
  const [companyConsent, setCompanyConsent] = useState(false)
  const [propertyConsent, setPropertyConsent] = useState(false)
  const [hasUserScrolled, setHasUserScrolled] = useState(false)
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)

  const isCompanyUser = (COMPANY_SPECIFIC_ROLES as readonly string[]).includes(roleName)
  const isPropertyUser = (PROPERTY_ROLES as readonly string[]).includes(roleName)

  const consentValid =
    (!isCompanyUser || companyConsent) && (!isPropertyUser || propertyConsent)

  const checkScrollPosition = () => {
    const el = contentRef.current
    if (!el) return
    if (el.scrollTop > 5) setHasUserScrolled(true)
    setHasScrolledToBottom(el.scrollHeight - el.scrollTop - el.clientHeight <= 50)
  }

  const clearPending = () => {
    try {
      sessionStorage.removeItem(GDPR_CONSENT_PENDING_KEY)
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    if (!isOpen) return

    let cancelled = false
    setLoading(true)
    setConsentDoc(null)
    setCompanyConsent(false)
    setPropertyConsent(false)
    setHasUserScrolled(false)
    setHasScrolledToBottom(false)

    void checkGdprConsent()
      .then((response) => {
        if (cancelled) return
        const detail = response.data
        if (detail?.content) {
          setConsentDoc(detail)
          window.setTimeout(checkScrollPosition, 100)
        } else {
          clearPending()
          onAccepted()
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const message =
          error instanceof Error ? error.message : 'Failed to load service agreement'
        if (message.toLowerCase().includes('already signed')) {
          showError('Already signed Acknowledgement.', false)
          clearPending()
          onAccepted()
        } else {
          showError(message)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const handleAccept = async () => {
    if (!consentDoc?.id || !consentValid) {
      showError('Please read the document and provide consent to continue', false)
      return
    }

    setSubmitting(true)
    try {
      await submitGdprConsent({
        document_id: Number(consentDoc.id),
        company_level_consent: companyConsent,
        property_level_consent: propertyConsent,
        user_agent: navigator.userAgent,
      })
      clearPending()
      showSuccess('Thank you for your acknowledgement of the Liberty Rent Service Agreement.')
      onAccepted()
    } catch (error) {
      showError(
        error instanceof Error
          ? error.message
          : 'Failed to submit service agreement acknowledgement',
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleDecline = () => {
    if (submitting) return
    setSubmitting(true)
    clearPending()
    void logoutAndRedirect()
  }

  if (!isOpen) return null

  const footer = (
    <div
      className={`${modalStyles.modalFooterActionGroup} ${modalStyles.modalFooterPrimaryFirst}`}
      data-testid="gdpr-consent-modal-footer"
    >
      <button
        type="button"
        onClick={handleAccept}
        disabled={!consentValid || submitting || loading || !consentDoc}
        className={modalStyles.modalActionPrimary}
        data-testid="gdpr-consent-accept"
      >
        {submitting ? 'Saving...' : 'Accept'}
      </button>
      <button
        type="button"
        onClick={handleDecline}
        disabled={submitting}
        className={modalStyles.modalActionPrimary}
        data-testid="gdpr-consent-decline"
      >
        Decline
      </button>
    </div>
  )

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="Acknowledgement Required: Liberty Rent Service Agreement"
      onClose={() => {
        /* Required acknowledgement — overlay / Esc must not dismiss. */
      }}
      hideClose
      maxWidth="max-w-900"
      panelClassName="!max-w-[min(1000px,calc(100vw-24px))]"
      bodyScrollOnly
      bodyClassName="!px-5 !py-4"
      overlayDataTestId="gdpr-consent-modal-overlay"
      panelDataTestId="gdpr-consent-modal-panel"
      titleDataTestId="gdpr-consent-modal-title"
      footer={footer}
    >
      {loading || !consentDoc ? (
        <ModuleScreenLoading variant="inline" data-testid="gdpr-consent-loading" />
      ) : (
        <div className="relative flex min-h-0 flex-col gap-3">
          {!hasUserScrolled && !hasScrolledToBottom ? (
            <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-center text-sm font-medium text-blue-700">
              Scroll down to continue
            </div>
          ) : null}

          {hasUserScrolled && !hasScrolledToBottom ? (
            <div className="rounded-md border border-amber-100 bg-amber-50 px-3 py-2 text-center text-sm font-medium text-amber-700">
              Please read the entire document.
            </div>
          ) : null}

          <ServiceAgreementHtml
            contentRef={contentRef}
            onScroll={checkScrollPosition}
            html={consentDoc.content}
            documentId={consentDoc.id}
            eorpSection={consentDoc.eorp_section}
            className="max-h-[min(52vh,480px)] min-h-[180px] overflow-y-auto rounded-md border border-slate-200 bg-white p-4 text-sm leading-6 text-slate-800"
            data-testid="gdpr-consent-document-content"
          />

          <div className="space-y-3 text-sm text-slate-700">
            {isCompanyUser ? (
              <label className="flex items-start gap-2 rounded border border-slate-200 bg-slate-50 p-2">
                <input
                  type="checkbox"
                  checked={companyConsent}
                  disabled={submitting || !hasScrolledToBottom}
                  onChange={(e) => setCompanyConsent(e.target.checked)}
                  className="mt-1"
                />
                <span>
                  I acknowledge that I have read and agree to the terms outlined in
                  Liberty Rent&apos;s Service Agreement. I acknowledge the Service
                  Agreement may change.
                </span>
              </label>
            ) : null}

            {isPropertyUser ? (
              <label className="flex items-start gap-2 rounded border border-slate-200 bg-slate-50 p-2">
                <input
                  type="checkbox"
                  checked={propertyConsent}
                  disabled={submitting || !hasScrolledToBottom}
                  onChange={(e) => setPropertyConsent(e.target.checked)}
                  className="mt-1"
                />
                <span>
                  I acknowledge that I have read and agree to the terms outlined in
                  Liberty Rent&apos;s Service Agreement. I acknowledge the Service
                  Agreement may change.
                </span>
              </label>
            ) : null}
          </div>
        </div>
      )}
    </OnScreenModal>
  )
}
